/* ==========================================================================
   WARMY - BUNDLE BUILDER

   Owns the mix-and-match interface. Kaching Bundles owns the pricing.

   THE DEFECT THIS FIXES: Kaching's builder discards per-item colour/size
   selections on tier change. Here the selection array only ever GROWS -
   4 -> 2 -> 4 restores the original four picks.

   PRE-FILLED ROWS (owner decision 2026-07-27): every row starts on a real
   colour and size rather than an empty "Choose" placeholder, so Add to cart
   is live immediately instead of gated behind four dropdowns. Colour seeds
   from the swatch the shopper actually clicked; size seeds to M when that
   combination is in stock, otherwise the first available size for that
   colour. The placeholder <option> is stripped so it can't be re-selected.

   KACHING CONTRACT (verified live 2026-07-25/26, corrected 2026-07-27):
   - fetchDeals() returns deals; the properties value uses the deal's nanoId
     (e.g. "NBnV"), plus bid = the deal-bar id for the chosen tier.
   - Kaching's embed loads asynchronously, so we POLL for kachingBundlesApi
     for up to 10s before giving up (a builder that races the embed otherwise
     renders no tier prices).
   - discount.type "amount" is per-unit off in SHOP BASE currency; multiply by
     Kaching's own market rate. "percentage" applies to the variant price.
   - *** main MUST BE true ON EVERY LINE OF THE BUNDLE. *** Kaching's discount
     function only treats main:true lines as entitled. Setting main:true on
     only the first line (the behaviour before 2026-07-27) meant a mixed 2- or
     4-pack reached the Shopify cart at FULL PRICE - $100.00 and $200.00 -
     while this interface advertised $77.80 and $111.60. Confirmed against
     Kaching's own native form, which emits main:true on all four lines of a
     mixed 4-pack.
   ========================================================================== */

(() => {
  'use strict';

  const STORAGE_KEY = 'warmy:bundle-selections';

  const uuid = () =>
    ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
      (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
    );

  const parseJSONScript = (root, selector) => {
    const el = root.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (err) { console.error(`[warmy] Could not parse ${selector}`, err); return null; }
  };

  const marketRate = () => {
    try {
      const cfg = JSON.parse(document.getElementById('kaching-bundles-config').textContent);
      return Number(cfg.currencyRate) || 1;
    } catch { return 1; }
  };

  const formatMoney = (cents, format) => {
    const amount = (cents / 100).toFixed(2);
    if (!format) return `$${amount}`;
    return format.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  };

  /** Kaching's embed loads async - wait for its API instead of racing it. */
  const waitForKaching = (timeoutMs) =>
    new Promise((resolve) => {
      const t0 = Date.now();
      const poll = () => {
        const api = window.kachingBundlesApi;
        if (api && typeof api.fetchDeals === 'function') return resolve(api);
        if (Date.now() - t0 > timeoutMs) return resolve(null);
        setTimeout(poll, 250);
      };
      poll();
    });

  class WarmyBundleBuilder extends HTMLElement {
    connectedCallback() {
      this.product = parseJSONScript(this, '[data-warmy-product-data]');
      this.shop = parseJSONScript(this, '[data-warmy-shop-context]');
      if (!this.product || !this.shop) {
        console.error('[warmy] Builder missing context; markup left untouched.');
        return;
      }

      this.stripPlaceholders();
      this.selections = this.restoreSelections();
      this.visibleCount = Number(this.dataset.defaultCount || 2);
      this.dealNanoId = this.dataset.dealId || null;
      this.rate = marketRate();
      this.activeDeal = null;

      this.addEventListener('change', this.handleChange);
      this.addEventListener('click', this.handleClick);

      this.render();
      this.loadDeals()
        .then(() => { this.rate = marketRate(); this.renderTierPrices(); })
        .catch((err) => {
          console.error('[warmy] Kaching deal fetch failed; tier prices hidden.', err);
          this.hideTierPrices();
        });
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('click', this.handleClick);
    }

    /** Rows ship pre-filled, so the empty "Choose" option must not be selectable. */
    stripPlaceholders() {
      this.querySelectorAll('[data-warmy-option] option[value=""]').forEach((o) => o.remove());
    }

    availableColors() {
      const ci = this.product.optionIndex.color;
      if (ci === null) return [];
      return [...new Set(this.product.variants.filter((v) => v.available).map((v) => v.options[ci]))];
    }

    availableSizes(color) {
      const { color: ci, size: si } = this.product.optionIndex;
      if (si === null) return [];
      return [...new Set(this.product.variants
        .filter((v) => v.available && (ci === null || !color || v.options[ci] === color))
        .map((v) => v.options[si]))];
    }

    /** Seed a row: the colour the shopper picked on the swatches, size M when stocked. */
    defaultSelection() {
      const colors = this.availableColors();
      const chip = document.querySelector('[data-product-color][aria-pressed="true"]')?.dataset.productColor;
      const color = (chip && colors.includes(chip)) ? chip : (colors[0] ?? null);
      const sizes = this.availableSizes(color);
      const size = sizes.includes('M') ? 'M' : (sizes[0] ?? null);
      return { color, size };
    }

    async loadDeals() {
      const api = await waitForKaching(10000);
      if (!api) throw new Error('kachingBundlesApi unavailable after 10s - is the app embed enabled?');
      const res = await api.fetchDeals({ productId: this.product.productId });
      const deals = Array.isArray(res) ? res : res?.deals ?? [];
      this.activeDeal = this.dealNanoId
        ? deals.find((d) => d.nanoId === this.dealNanoId) ?? null
        : deals[0] ?? null;
      if (this.dealNanoId && !this.activeDeal) {
        throw new Error(`Deal "${this.dealNanoId}" not found among ${deals.length} returned deals.`);
      }
      return this.activeDeal;
    }

    barFor(count) {
      const bars = this.activeDeal?.dealBars ?? [];
      return bars
        .filter((b) => b.dealBarType === 'quantity-break' && b.quantity <= count)
        .sort((a, b) => b.quantity - a.quantity)[0] ?? null;
    }

    perUnitCents(count, baseCents) {
      const bar = this.barFor(count);
      if (!bar || !bar.discount) return baseCents;
      if (bar.discount.type === 'percentage') {
        return Math.round(baseCents * (1 - Number(bar.discount.value) / 100));
      }
      const offCents = Math.round(Number(bar.discount.value) * 100 * this.rate);
      return Math.max(0, baseCents - offCents);
    }

    /**
     * main is ALWAYS true - see the KACHING CONTRACT note at the top of this file.
     * Do not reintroduce a "first line only" flag; it silently disables the discount.
     */
    kachingProperty(bundleId, count) {
      if (!this.activeDeal) return null;
      const bar = this.barFor(count);
      const prop = {
        deal: this.activeDeal.nanoId,
        pp: this.product.productId,
        main: true,
        v: bundleId
      };
      if (bar && bar.id) prop.bid = bar.id;
      return JSON.stringify(prop);
    }

    restoreSelections() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return parsed.productId === this.product.productId ? parsed.selections : [];
      } catch { return []; }
    }

    persistSelections() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          productId: this.product.productId,
          selections: this.selections
        }));
      } catch { /* private mode */ }
    }

    /** Rows are never empty now - an unseeded slot gets sensible defaults. */
    selectionAt(index) {
      const current = this.selections[index];
      if (!current) {
        this.selections[index] = this.defaultSelection();
      } else {
        if (!current.color || !current.size) {
          const fallback = this.defaultSelection();
          if (!current.color) current.color = fallback.color;
          if (!current.size) current.size = this.availableSizes(current.color).includes('M')
            ? 'M'
            : (this.availableSizes(current.color)[0] ?? fallback.size);
        }
      }
      return this.selections[index];
    }

    setVisibleCount(count) {
      const next = Math.max(1, Number(count) || 1);
      while (this.selections.length < next) this.selections.push(this.defaultSelection());
      this.visibleCount = next;
      this.persistSelections();
      this.render();
    }

    variantFor(sel) {
      const { color: ci, size: si } = this.product.optionIndex;
      if (ci === null || si === null || !sel) return null;
      return this.product.variants.find(
        (v) => v.options[ci] === sel.color && v.options[si] === sel.size
      ) ?? null;
    }

    isCombinationAvailable(color, size) {
      const v = this.variantFor({ color, size });
      return Boolean(v && v.available);
    }

    resolvedLines() {
      return this.selections.slice(0, this.visibleCount)
        .map((s) => this.variantFor(s))
        .filter((v) => v && v.available);
    }

    baseUnitCents() {
      const first = this.resolvedLines()[0];
      if (first) return first.price;
      const cheapest = this.product.variants.filter((v) => v.available)
        .sort((a, b) => a.price - b.price)[0];
      return cheapest ? cheapest.price : 0;
    }

    handleChange = (event) => {
      const target = event.target;
      if (target.matches('[data-warmy-tier]')) { this.setVisibleCount(target.value); return; }
      const row = target.closest('[data-warmy-row]');
      if (row && target.matches('[data-warmy-option]')) {
        const sel = this.selectionAt(Number(row.dataset.warmyRow));
        sel[target.dataset.warmyOption] = target.value || null;
        /* Changing colour can strand an out-of-stock size - move to a stocked one. */
        if (target.dataset.warmyOption === 'color' && sel.size
            && !this.isCombinationAvailable(sel.color, sel.size)) {
          const sizes = this.availableSizes(sel.color);
          sel.size = sizes.includes('M') ? 'M' : (sizes[0] ?? null);
        }
        this.persistSelections();
        this.render();
      }
    };

    handleClick = (event) => {
      if (event.target.closest('[data-warmy-add]')) {
        event.preventDefault();
        this.addToCart();
      }
    };

    /** Any multi-item tier is advertising a bundle price on screen. */
    promisesDiscount(count) {
      return Number(count) > 1;
    }

    async addToCart() {
      const lines = this.resolvedLines();
      if (lines.length !== this.visibleCount) return;

      /* ------------------------------------------------------------------
         FAIL CLOSED. Never add at full price when the interface promised a
         bundle price. If the deal state is missing we retry the fetch once,
         and if it still is not there we add NOTHING and say so.
         ------------------------------------------------------------------ */
      if (!this.activeDeal && this.promisesDiscount(this.visibleCount)) {
        this.setBusy(true);
        this.setStatus(this.dataset.retryingMessage || 'Getting your bundle price...');
        try { await this.loadDeals(); }
        catch (err) { console.error('[warmy] Kaching retry failed', err); }
        finally { this.setBusy(false); }

        if (!this.activeDeal) {
          this.setStatus(this.dataset.dealUnavailableMessage
            || 'We could not load your bundle price. Nothing was added - please refresh and try again.');
          document.dispatchEvent(new CustomEvent('warmy:bundle-unavailable', { bubbles: true }));
          return;
        }
        this.rate = marketRate();
        this.renderTierPrices();
        this.setStatus('');
      }

      const bundleId = uuid();
      const prop = this.kachingProperty(bundleId, this.visibleCount);
      const items = lines.map((variant) => {
        const item = { id: variant.id, quantity: 1 };
        if (prop) item.properties = { __kaching_bundles: prop };
        return item;
      });

      const expectedCents = this.perUnitCents(this.visibleCount, this.baseUnitCents()) * this.visibleCount;

      this.setBusy(true);
      this.setStatus('');
      try {
        const res = await fetch(this.shop.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        if (!res.ok) throw new Error(`Cart add failed: ${res.status}`);

        /* Verify the discount actually landed. This is the check that would have
           caught the main:false regression before it ever reached a shopper. */
        this.verifyCartPrice(expectedCents);

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('warmy:added', { bubbles: true, detail: { items } }));
      } catch (err) {
        console.error('[warmy] Add to cart failed', err);
        this.setStatus(this.dataset.errorMessage || 'Something went wrong. Please try again.');
      } finally {
        this.setBusy(false);
      }
    }

    /**
     * Post-add assertion. Reads the real Shopify cart and compares what the
     * shopper was promised against what Shopify actually charged. Never blocks
     * the shopper - it raises a loud console error and a DOM event so a broken
     * discount contract surfaces in QA instead of in the order data.
     */
    async verifyCartPrice(expectedCents) {
      if (!this.promisesDiscount(this.visibleCount)) return;
      try {
        const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
        if (cart.total_discount === 0 && expectedCents < cart.original_total_price) {
          console.error(
            '[warmy] BUNDLE PRICE MISMATCH - Shopify applied no discount.',
            { expectedCents, cartTotal: cart.total_price, original: cart.original_total_price }
          );
          document.dispatchEvent(new CustomEvent('warmy:bundle-price-mismatch', {
            bubbles: true,
            detail: { expectedCents, cartTotal: cart.total_price }
          }));
        }
      } catch { /* verification is diagnostic only */ }
    }

    setBusy(busy) {
      this.toggleAttribute('data-busy', busy);
      this.querySelectorAll('[data-warmy-add]').forEach((b) => {
        b.disabled = busy;
        b.setAttribute('aria-busy', String(busy));
      });
    }

    setStatus(message) {
      const el = this.querySelector('[data-warmy-status]');
      if (el) el.textContent = message;
    }

    render() {
      this.renderRows();
      this.renderAddButton();
      if (this.activeDeal) this.renderTierPrices();
    }

    renderRows() {
      this.querySelectorAll('[data-warmy-row]').forEach((row) => {
        const index = Number(row.dataset.warmyRow);
        const visible = index < this.visibleCount;
        row.hidden = !visible;
        row.querySelectorAll('select, input, button').forEach((el) => { el.disabled = !visible; });
        if (!visible) return;

        const sel = this.selectionAt(index);
        row.querySelectorAll('[data-warmy-option]').forEach((field) => {
          const kind = field.dataset.warmyOption;
          const value = sel[kind] ?? '';
          if (field.value !== value) field.value = value;
          if (kind === 'size') this.markUnavailableSizes(field, sel.color);
        });
      });
      this.persistSelections();
    }

    markUnavailableSizes(select, color) {
      select.querySelectorAll('option[value]').forEach((opt) => {
        if (!opt.value) return;
        opt.disabled = color ? !this.isCombinationAvailable(color, opt.value) : false;
      });
    }

    renderTierPrices() {
      const base = this.baseUnitCents();
      const fmt = this.shop.moneyFormat;
      this.querySelectorAll('[data-warmy-tier-price]').forEach((el) => {
        const count = Number(el.dataset.warmyTierPrice);
        const unit = this.perUnitCents(count, base);
        el.innerHTML = '';
        const total = document.createElement('strong');
        total.textContent = formatMoney(unit * count, fmt);
        const each = document.createElement('span');
        each.textContent = `${formatMoney(unit, fmt)} each`;
        el.append(total, each);
      });
    }

    hideTierPrices() {
      this.querySelectorAll('[data-warmy-tier-price]').forEach((el) => { el.textContent = ''; });
    }

    renderAddButton() {
      const button = this.querySelector('[data-warmy-add]');
      if (!button) return;
      const remaining = this.visibleCount - this.resolvedLines().length;
      button.disabled = remaining > 0;
      const label = button.querySelector('[data-warmy-add-label]');
      if (!label) return;
      label.textContent = remaining > 0
        ? (button.dataset.incompleteLabel || 'Choose [count] more').replace(/\[count\]/g, String(remaining))
        : (button.dataset.completeLabel || 'Add to cart');
    }
  }

  if (!customElements.get('warmy-bundle-builder')) {
    customElements.define('warmy-bundle-builder', WarmyBundleBuilder);
  }
})();
